import React from 'react';
import { Form, Input, Col, Row, Select, Checkbox } from 'antd';
import { CaretDown, Check, PencilSimple, Plus, Trash, X, SpinnerGap } from '@phosphor-icons/react';
import { ErrorMessage, SwitchCommon, InputCommon, SelectCommon, PrefixedInput } from 'components/common';
import { CategoryFormSectionsProps } from 'types';
export const CategoryFormSections: React.FC<CategoryFormSectionsProps> = ({
  activeForm,
  codePrefix,
  setCodePrefix,
  onGenerate,
  editMode,
  isCopyMode,
  rowValues,
  confirmedRows,
  handleInputChange,
  confirmRow,
  unconfirmRow,
  removeRow,
  addNewRow,
  selectedValues,
  isAttributesLoading,
  isActive,
  attributeOptions,
  shouldLoadAttributes,
  setShouldLoadAttributes,
  setSelectedValues,
  handleAttributeSearch,
  handleToggleActive,
  handleSubmit,
}) => {
  const handleDimensionsPlusClick = () => {
    const input = document.querySelector('#productDimensions') as HTMLInputElement;
    input.blur();
    input.focus();
  };

  // Tương tự cho sizeSpecs
  const handleSizeSpecsPlusClick = () => {
    const input = document.querySelector('#sizeSpecs') as HTMLInputElement;
    input.blur();
    input.focus();
  };
  return (
    <Form form={activeForm} layout='vertical' className='!mt-2 !pl-1' onFinish={handleSubmit} requiredMark={false}>
      <Form.Item
        name='name'
        label={
          <span className='relative text-[14px] leading-[20px]'>
            Tên danh mục
            <span className='absolute -top-1 -right-3 text-red-500 text-[8px] font-medium'>✱</span>
          </span>
        }
        rules={[
          {
            required: true,
            message: <ErrorMessage message='Giá trị này là bắt buộc, bạn phải nhập giá trị!' />,
          },
          { max: 100, message: <ErrorMessage message='Tên danh mục không được vượt quá 100 ký tự!' /> },
        ]}
      >
        <InputCommon
          className='!border-0 !pl-0 rounded-none font-bold !text-[20px] !leading-[24px] !border-b-[1px] border-[var(--textBlack12)] !transition-none !shadow-none'
          placeholder='Nhập vào tên danh mục'
          radius='none'
          maxLength={100}
        />
      </Form.Item>

      <Row gutter={24}>
        <Col xl={6} lg={10} md={12} xs={24}>
          <Form.Item
            name='code'
            label={
              <span className='relative text-[14px] leading-[20px]'>
                Mã danh mục
                <span className='absolute -top-1 -right-3 text-red-500 text-[8px] font-medium'>✱</span>
              </span>
            }
            rules={[
              {
                required: true,
                message: <ErrorMessage message='Giá trị này là bắt buộc, bạn phải nhập giá trị!' />,
              },
              { max: 12, message: <ErrorMessage message='Mã danh mục không được vượt quá 12 ký tự!' /> },
            ]}
            className='!mb-2'
          >
            <PrefixedInput
              prefix={codePrefix}
              maxEditableLength={6}
              placeholder='Nhập mã'
              disabled={editMode && !isCopyMode}
              className='!text-[14px] !leading-[20px]'
              inputProps={{
                suffix: (!editMode || isCopyMode) && (
                  <span
                    className='text-[var(--primaryColor500)] cursor-pointer !text-[14px] !leading-[20px]'
                    onClick={async (e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (onGenerate) {
                        try {
                          const code = await onGenerate();
                          if (code && code.length >= 6) {
                            setCodePrefix(code.substring(0, 6));
                            activeForm.setFieldsValue({ code });
                          }
                        } catch (error) {
                          console.error('Error generating code:', error);
                        }
                      }
                    }}
                  >
                    Generate
                  </span>
                ),
              }}
            />
          </Form.Item>
        </Col>
        <Col xl={18} lg={14} md={12} xs={24}>
          <Form.Item
            name='isActive'
            valuePropName='checked'
            initialValue={isActive}
            label={<span className='text-[14px] leading-[20px]'>Trạng thái</span>}
            className='py-1.5 !mb-1'
          >
            <SwitchCommon
              label={isActive ? 'Kích hoạt' : 'Chưa kích hoạt'}
              disabled={editMode}
              isActive={isActive}
              onChange={editMode ? undefined : handleToggleActive}
            />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={24}>
        <Col xl={6} lg={10} md={12} xs={24}>
          <Form.Item
            name='categoryAttributes'
            label={<span className='relative text-[14px] leading-[20px]  font-medium'>Thuộc tính</span>}
            className='!mb-2'
          >
            <SelectCommon
              placeholder='Chọn thuộc tính'
              className='!text-[14px] xl:!h-[40px] multi-select-custom'
              suffixIcon={<CaretDown size={16} />}
              options={attributeOptions}
              mode='multiple'
              onChange={(values) => setSelectedValues(values)}
              allowClear
              maxTagCount={0}
              maxTagPlaceholder={() => `${selectedValues.length} thuộc tính`}
              showSearch
              filterOption={false}
              onSearch={handleAttributeSearch}
              loading={shouldLoadAttributes && isAttributesLoading}
              onFocus={() => setShouldLoadAttributes(true)}
              notFoundContent={
                shouldLoadAttributes && isAttributesLoading ? (
                  <div className='flex justify-start items-center'>
                    <SpinnerGap size={18} weight='bold' className='text-[var(--primaryColor500)] animate-spin' />
                    <span className='ml-2'>Đang tải...</span>
                  </div>
                ) : (
                  'Không có dữ liệu'
                )
              }
            />
          </Form.Item>
        </Col>
        <Col xl={18} lg={14} md={12} xs={24}>
          <Form.Item
            name='sizeSpecs'
            label={<span className='relative text-[14px] leading-[20px] font-medium'>Độ dày</span>}
            className='!mb-2'
          >
            <Select
              mode='tags'
              style={{ width: '100%' }}
              placeholder='Nhập độ dày'
              className='tags-select-custom tags-select-with-scroll'
              suffixIcon={
                <button
                  className='text-[var(--primaryColor500)] cursor-pointer z-10'
                  onClick={handleSizeSpecsPlusClick}
                  type='button'
                >
                  <Plus size={16} />
                </button>
              }
              maxTagCount={7}
              maxTagPlaceholder={(omittedValues) => `+${omittedValues.length} độ dày khác`}
              // open={true}
              listHeight={200}
              popupMatchSelectWidth={false}
            />
          </Form.Item>
        </Col>
      </Row>
      <Form.Item
        name='productDimensions'
        label={<span className='relative text-[14px] leading-[20px] font-medium'>Quy cách sản phẩm</span>}
      >
        <Select
          mode='tags'
          style={{ width: '100%' }}
          placeholder='Nhập quy cách sản phẩm'
          className='tags-select-custom tags-select-with-scroll'
          suffixIcon={
            <button
              className='text-[var(--primaryColor500)] cursor-pointer z-10'
              onClick={handleDimensionsPlusClick}
              type='button'
            >
              <Plus size={16} />
            </button>
          }
          maxTagCount={7}
          maxTagPlaceholder={(omittedValues) => `+${omittedValues.length} quy cách khác`}
          // open={false}
          listHeight={200}
          popupMatchSelectWidth={false}
        />
      </Form.Item>
      <Form.Item
        label={<span className='text-[14px] font-medium'>Đơn vị đặc thù</span>}
        className='mb-2 bg-[var(--textBlack2)] p-2 rounded-lg '
      >
        <Form.List name='categoryUnits'>
          {(fields, { add, remove }) => (
            <div className='flex flex-col gap-2'>
              {fields.map((field, idx) => {
                return (
                  <div
                    key={field.key}
                    className='grid grid-cols-12 justify-start items-center gap-3 px-3 py-0  bg-gray-50 rounded-lg'
                  >
                    <div className='col-span-2 flex items-center gap-2 !h-[30px]'>
                      <Form.Item name={[field.name, 'checked']} valuePropName='checked' noStyle>
                        <Checkbox />
                      </Form.Item>
                      <Form.Item
                        name={[field.name, 'name']}
                        className='!m-0 flex items-center custom-add-category-unit-input'
                      >
                        <Input className='w-full' placeholder='Tên đơn vị' />
                      </Form.Item>
                    </div>

                    <span className='text-black/60 text-sm col-span-1'>giá trị quy đổi:</span>

                    <Form.Item
                      shouldUpdate={(prev, curr) =>
                        prev.categoryUnits?.[idx]?.checked !== curr.categoryUnits?.[idx]?.checked
                      }
                      noStyle
                    >
                      {() => (
                        <Form.Item name={[field.name, 'value']} className='col-span-2 flex !m-0 !h-[30px]'>
                          <Input className='w-52' placeholder='---' />
                        </Form.Item>
                      )}
                    </Form.Item>

                    <Form.Item
                      shouldUpdate={(prev, curr) =>
                        prev.categoryUnits?.[idx]?.checked !== curr.categoryUnits?.[idx]?.checked
                      }
                      noStyle
                    >
                      {() => (
                        <Form.Item name={[field.name, 'unit']} className='col-span-2 flex !m-0' initialValue='Kg'>
                          <Select
                            className='!w-52'
                            options={[{ label: 'Kg', value: 'Kg' }]}
                            defaultValue='Kg'
                            placeholder='Đơn vị'
                            disabled={true}
                          />
                        </Form.Item>
                      )}
                    </Form.Item>

                    <div className='col-span-2 w-14 h-9 px-2 flex justify-center items-center gap-2'>
                      <>
                        <X size={18} className='text-rose-500 cursor-pointer' onClick={() => remove(field.name)} />
                      </>
                    </div>
                  </div>
                );
              })}

              <button
                type='button'
                className='h-8 max-w-fit px-2 py-2.5 bg-black/5 rounded-lg flex items-center gap-2.5 '
                onClick={() => add({ type: '', checked: false, value: '', unit: 'Kg' })}
              >
                <Plus size={20} className='text-black' />
                <span className='text-black text-sm'>Thêm đơn vị đặc thù</span>
              </button>
            </div>
          )}
        </Form.List>
      </Form.Item>

      <div className='mb-4'>
        <h4 className='text-[14px] font-medium mb-2'>Quy cách bán hàng</h4>

        <div className='flex flex-col gap-2 bg-[var(--textBlack2)] p-2 rounded-lg'>
          {Object.entries(rowValues).map(([indexStr, values]) => {
            const rowIndex = parseInt(indexStr);
            const isConfirmed = confirmedRows.includes(rowIndex);

            return (
              <div
                key={rowIndex}
                className='self-stretch inline-flex justify-start items-start gap-2 bg-gray-50 p-2 rounded-xl'
              >
                {[0, 1, 2, 3].map((colIndex) => (
                  <InputCommon
                    key={colIndex}
                    maxLength={50}
                    className='w-full text-[14px] leading-[20px] text-[var(--textBlack100)] p-2 rounded-lg'
                    placeholder={isConfirmed ? '' : `Nhập thông số #${colIndex + 1}`}
                    value={values[colIndex]}
                    onChange={(e) => {
                      handleInputChange(rowIndex, colIndex, e.target.value);
                    }}
                    type={'text'}
                    disabled={isConfirmed}
                  />
                ))}

                <div className='w-14 h-9 px-2 flex justify-center items-center gap-2'>
                  {!isConfirmed ? (
                    <>
                      <Check
                        size={20}
                        className='text-green-500 cursor-pointer'
                        onClick={() => {
                          confirmRow(rowIndex);
                        }}
                      />
                      <X size={18} className='text-rose-500 cursor-pointer' onClick={() => removeRow(rowIndex)} />
                    </>
                  ) : (
                    <>
                      <PencilSimple
                        size={18}
                        className='text-black cursor-pointer'
                        onClick={() => unconfirmRow(rowIndex)}
                      />
                      <Trash size={18} className='text-rose-500 cursor-pointer' onClick={() => removeRow(rowIndex)} />
                    </>
                  )}
                </div>
              </div>
            );
          })}

          <button
            type='button'
            className={`h-8 px-2 py-2.5 max-w-fit bg-black/5 rounded-lg flex items-center gap-2.5 mt-2 ${
              Object.keys(rowValues).length >= 1 ? 'opacity-60 cursor-not-allowed' : ''
            }`}
            onClick={addNewRow}
            disabled={Object.keys(rowValues).length >= 1}
          >
            <Plus size={20} className='text-black' />
            <span className='text-black text-sm'>Thêm quy cách</span>
          </button>
        </div>
      </div>
      <Form.Item name='saleDimensionsData' hidden noStyle>
        <Input type='hidden' />
      </Form.Item>
    </Form>
  );
};
